import createDebug from 'debug';
import { Deserializer } from "jsonapi-serializer";
import { INTERNAL_SERVER_ERROR, NO_CONTENT } from "../constants/response-status-codes";
import { CardType, CardTypeWrapper } from "../models/CardType";
import { CardTypeName, CardTypeNameWrapper } from "../models/CardTypeName";
import { genericError } from "../models/GenericError";
import { ListQuery } from "../models/ListQuery";
import { StoreEntityResult } from "../models/services/StoreEntityResult";
import { APIConnector } from "./api/APIConnector";
import { createPagination } from "./utilities/paginationBuilder";
import { processServerError, getConstraintViolation } from "./utilities/ServiceError";
import { createPartialURL, getFilterClause } from "./utilities/urlBuilder";
import { Name } from '@/models/Name';
import { ListData } from '@/models/ListData';

const apiConnector = new APIConnector();
const debug = createDebug('card-format-service');

export const updateCardType = async (card, createCardType = false) => {
    const newRecord = {
        data: {
            type: "tagType",
            id: card.cardTypeId,
            attributes: {
                ttDesc: null,
            },
            relationships: {
                truncationFormat: {
                    data: {
                        type: "truncationFormat",
                        id: card.truncationFormatId,
                    }
                }
            }
        }
    }
    const url = `tagType/${card.cardTypeId}`;
    let response: Response;

    await apiConnector.sendData(url, false, newRecord).then(data => {
        response = data;
    }).catch(error => {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        })
    });

    return new Promise<StoreEntityResult>(async (resolve, reject) => {
        if (response) {
            if (createCardType) {
                const content = await response.json();
                if (content.errors) {
                    return reject({status: response.status, message: getConstraintViolation(content.errors[0].detail) })
                }

                new Deserializer().deserialize(content, (error) => {
                    if (error) {
                        return reject({status: response.status, id: card.cardTypeid, success: false });
                    }

                    return resolve({ id: card.cardTypeid, success: true });
                })

                return
            }

            if (response.status === NO_CONTENT) {
                return resolve({ id: card.cardTypeid, success: true })
            }

            const json = await response.json();
            return reject({status: response.status, id: card.cardTypeid, success: false, message: getConstraintViolation(json.errors[0].detail) });
        }

        return reject({status: INTERNAL_SERVER_ERROR, id: card.cardTypeid, success: false });
    })
}

export const getCardTypes = async (itemId: string, listParameters?: ListQuery): Promise<CardTypeWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const withAdditionalFilter = `${filter ? filter.replace('filter=', `filter=truncationFormat.id==${itemId};`) : `&filter=truncationFormat.id==${itemId}`}`;
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, withAdditionalFilter);
        const url = `tagType?fields[tagType]=trnName,totalTags&${paginationString}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    debug('getCardTypes', error);
                    return reject({status: response.status, message: genericError });;
                }

                const cardType: CardType[] = data.map((cardType): CardType => {
                    return {
                        id: cardType['id'],
                        name: cardType['trn-name'],
                        cards: cardType['total-tags']
                    }
                })

                return resolve({ rows: cardType, pagination });
            })
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getAllCardTypes = async (listParameters?: ListQuery): Promise<CardTypeNameWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const url = `tagType?${paginationString}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({status: response.status, message: genericError });;
                }

                const cardType: CardTypeName[] = data.map((cardType): CardTypeName => {
                    return {
                        id: cardType['id'],
                        name: cardType['trn-name'],
                    }
                })

                return resolve({ rows: cardType, pagination });
            })
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getCardTypesNamesWithPagination = async (listParameters?: ListQuery): Promise<ListData> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const url = `tagType?fields[tagType]=trnName${paginationString}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const cardTypeNames: Name[] = data.map((cardType): Name => {
                    return {
                        value: cardType['id'],
                        label: cardType['trn-name'],
                    }
                })

                return resolve({ rows: cardTypeNames, pagination: pagination });
            })
        });
    } catch (error) {
        return Promise.reject(processServerError(error))
    }
}
